<script setup lang="ts">
import { computed } from 'vue';
import { Download, Eye, ShoppingCart } from 'lucide-vue-next';
import LogoGlyph from '@/assets/logo-glyph.svg';
import Icon from '@/components/Icon.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import useDownloads from '@/composables/useDownloads.ts';
import useFile from '@/composables/useFile.ts';
import type { Badge as BadgeType, Element, Preview } from '@/types';

const props = withDefaults(
    defineProps<{
        type?: Element['type'];
        uid?: Element['uid'];
        title?: string;
        subtitle?: string | null;
        variant?: 'default' | 'sm';
        url?: string;
        directLink?: string | null;
        directLinkTarget?: string | null;
        shopUrl?: string;
        preview?: Preview | null;
        badge?: BadgeType | false | null;
        showActionButtons?: boolean;
        isNew?: boolean;
    }>(),
    {
        type: 'generic',
        variant: 'default',
        showActionButtons: false,
        isNew: false,
    }
);

const linkHref = computed(() => props.directLink ?? props.url);
const linkTarget = computed(() =>
    props.directLink ? (props.directLinkTarget ?? '_self') : undefined
);

const emit = defineEmits<{
    select: [uid?: string];
}>();

const { view } = useFile();
const { trackDownload } = useDownloads();

const onClick = (e: PointerEvent) => {
    emit('select', props.uid);

    if (props.type !== 'file' || !props.uid) {
        return;
    }

    e.preventDefault();
    view(props.uid);
};
</script>

<template>
    <Card
        class="group hover:bg-muted/25 relative flex-row py-0 transition-colors"
        :class="{
            'gap-0': variant === 'default',
            'items-center gap-3 px-3 py-2.5': variant === 'sm',
        }"
    >
        <div
            v-if="badge !== false || (preview && variant === 'default')"
            class="relative flex flex-none items-center justify-center"
            :class="{
                'bg-muted w-20 border-r p-3 [&_svg]:size-8':
                    variant === 'default',
                'bg-card size-9 rounded-md border [&_svg]:size-4':
                    variant === 'sm',
            }"
        >
            <img
                v-if="preview && variant === 'default'"
                :src="preview.src"
                :srcset="preview.srcset"
                alt="Dateivorschau"
                class="shadow-xs"
            />
            <Tooltip
                v-else-if="variant === 'sm' && badge?.icon && badge?.label"
            >
                <TooltipTrigger>
                    <Icon
                        :icon="badge?.icon ?? 'file'"
                        class="relative z-10"
                    />
                </TooltipTrigger>
                <TooltipContent class="text-ellipsis">
                    {{ badge?.label }}
                </TooltipContent>
            </Tooltip>
            <Icon
                v-else-if="variant === 'sm' && badge?.icon"
                :icon="badge.icon"
                class="relative z-10"
            />
            <LogoGlyph
                v-else
                class="text-muted-foreground"
            />
        </div>
        <div
            class="flex flex-1 flex-col gap-1 overflow-hidden"
            :class="{
                'p-small justify-between gap-y-3 px-4 py-3':
                    variant === 'default',
                'p-mini justify-center': variant === 'sm',
            }"
        >
            <div>
                <div class="flex items-center gap-2">
                    <p class="line-clamp-2 flex-1 font-semibold text-ellipsis">
                        <component
                            :is="linkHref ? 'a' : 'span'"
                            v-bind="
                                linkHref
                                    ? {
                                          onClick: onClick,
                                          href: linkHref,
                                          target: linkTarget,
                                      }
                                    : {}
                            "
                        >
                            <slot name="title">
                                {{ title ?? '' }}
                            </slot>
                        </component>
                    </p>
                    <Badge
                        v-if="variant === 'sm' && isNew"
                        variant="secondary"
                        >Neu</Badge
                    >
                </div>
                <p
                    v-if="subtitle"
                    class="text-muted-foreground"
                >
                    {{ subtitle }}
                </p>
            </div>
            <slot />
            <div class="flex gap-1">
                <Badge
                    v-if="variant === 'default' && isNew"
                    variant="secondary"
                    >Neu</Badge
                >
                <Badge
                    v-if="variant === 'default' && badge"
                    variant="outline"
                >
                    <Icon
                        v-if="badge.icon"
                        :icon="badge.icon"
                    />
                    <span>{{ badge.label }}</span>
                </Badge>
                <Button
                    v-if="shopUrl"
                    variant="ghost"
                    size="icon-sm"
                    as-child
                >
                    <a
                        :href="shopUrl"
                        target="_blank"
                    >
                        <ShoppingCart />
                    </a>
                </Button>
            </div>
        </div>
        <div
            v-if="showActionButtons && type === 'file'"
            class="flex gap-1"
        >
            <Button
                variant="ghost"
                size="icon-sm"
                @click="onClick"
            >
                <Eye />
            </Button>
            <Button
                variant="ghost"
                size="icon-sm"
                as-child
            >
                <a
                    :href="url"
                    download
                    @click="trackDownload(uid)"
                >
                    <Download />
                </a>
            </Button>
        </div>
        <a
            v-if="linkHref && !showActionButtons"
            :href="linkHref"
            :target="linkTarget"
            class="absolute inset-0"
            @click="onClick"
            ><span class="sr-only">Öffnen</span></a
        >
    </Card>
</template>
